import React, { useEffect } from "react";

/**
 * The one dialog shell for the whole app.
 *
 * Before this existed every modal brought its own overlay, radius, shadow and
 * header treatment - solid red banners next to grey ones, rounded-lg next to
 * rounded-2xl, six different backdrop opacities. Anything dialog-shaped should
 * render through here so they stay identical.
 *
 * Layout: fixed header, scrolling body, fixed footer. Only the body scrolls,
 * so the title and the actions stay visible on long content.
 */

const SIZES = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
  "2xl": "max-w-4xl",
};

const TONES = {
  default: { iconWrap: "bg-sunrise-red/10", icon: "text-sunrise-red" },
  danger: { iconWrap: "bg-sunrise-red/10", icon: "text-sunrise-red" },
  warning: { iconWrap: "bg-amber-100", icon: "text-amber-700" },
  success: { iconWrap: "bg-green-100", icon: "text-green-700" },
  neutral: {
    iconWrap: "bg-ice-white border border-pale-dawn",
    icon: "text-slate-cloud",
  },
};

export default function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  tone = "default",
  size = "lg",
  children,
  footer,
  // Set false for dialogs that must not be dismissed by accident, e.g. a
  // running action or a forced step.
  closeOnBackdrop = true,
  closeOnEscape = true,
  showClose = true,
  zIndex = 9999,
}) {
  useEffect(() => {
    if (!isOpen || !closeOnEscape || !onClose) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeOnEscape, onClose]);

  // A dialog over a scrolled page should not scroll the page behind it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const toneClasses = TONES[tone] || TONES.default;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      style={{ zIndex }}
      onMouseDown={(event) => {
        if (closeOnBackdrop && onClose && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={`bg-white rounded-2xl shadow-brand-xl border border-pale-dawn w-full ${
          SIZES[size] || SIZES.lg
        } max-h-[90vh] flex flex-col overflow-hidden`}
      >
        {(title || showClose) && (
          <div className="px-5 py-4 border-b border-pale-dawn bg-gradient-to-r from-ice-white to-pale-dawn/30 flex items-start justify-between gap-4 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              {icon && (
                <span
                  className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${toneClasses.iconWrap}`}
                >
                  <i className={`fas ${icon} ${toneClasses.icon}`}></i>
                </span>
              )}
              <div className="min-w-0">
                {title && (
                  <h2 className="text-lg font-bold text-grey-dusk leading-snug">
                    {title}
                  </h2>
                )}
                {subtitle && (
                  <p className="text-sm text-slate-cloud truncate">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>

            {showClose && onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="shrink-0 w-9 h-9 rounded-xl border border-pale-dawn bg-white text-gray-400 hover:text-grey-dusk hover:bg-pale-dawn/40 transition-colors flex items-center justify-center"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        )}

        <div className="p-5 overflow-y-auto flex-1">{children}</div>

        {footer && (
          <div className="px-5 py-4 border-t border-pale-dawn bg-ice-white flex flex-col sm:flex-row gap-3 sm:justify-end shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/** Cancel / dismiss action. Always the left-hand button in a footer. */
export function ModalSecondaryButton({ children, className = "", ...props }) {
  return (
    <button
      type="button"
      className={`btn-brand-outline sm:w-auto w-full ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

/** The action the dialog exists for. Always the right-hand button. */
export function ModalPrimaryButton({
  children,
  tone = "default",
  loading = false,
  loadingText = "Working...",
  className = "",
  disabled,
  ...props
}) {
  const toneClass =
    tone === "danger"
      ? "bg-sunrise-red hover:bg-sunrise-600"
      : tone === "warning"
        ? "bg-amber-600 hover:bg-amber-700"
        : tone === "success"
          ? "bg-green-600 hover:bg-green-700"
          : "bg-sunrise-red hover:bg-sunrise-600";

  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={`sm:w-auto w-full px-5 py-2.5 rounded-lg text-white text-sm font-semibold transition-colors shadow-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${toneClass} ${className}`}
      {...props}
    >
      {loading ? (
        <>
          <i className="fas fa-circle-notch fa-spin"></i>
          {loadingText}
        </>
      ) : (
        children
      )}
    </button>
  );
}
